import { useEffect } from 'react'
import type { Collection } from '@/types/terms'

const STORAGE_KEY = 'kva-active-collection'

function remembered(): number | null {
  const raw = Number(localStorage.getItem(STORAGE_KEY))
  return Number.isFinite(raw) && raw > 0 ? raw : null
}

/**
 * Which collection the app is working in.
 *
 * Precedence: the URL wins (a shared link must open what it says), then what
 * the user last picked, then simply the first one they can see. There is no
 * "default collection" any more — it granted access by a schema flag rather
 * than by anyone's decision. The remembered id is validated against the
 * collections actually available — access can be revoked, and a stale id would
 * otherwise 404 on every request.
 */
export function useActiveCollection(
  urlValue: number | null,
  collections: Collection[] | undefined,
): number | null {
  const known = collections?.some((c) => c.id === urlValue) ? urlValue : null
  const remembered_ = remembered()
  const rememberedValid = collections?.some((c) => c.id === remembered_)
    ? remembered_
    : null
  const fallback = collections?.[0]?.id ?? null
  const active = known ?? rememberedValid ?? fallback

  // Persist whatever ended up active, so the next page opens in the same place.
  useEffect(() => {
    if (active) localStorage.setItem(STORAGE_KEY, String(active))
  }, [active])

  return active
}

/** Read it outside React (e.g. to pre-fill a commit target). */
export function rememberedCollectionId(): number | null {
  return remembered()
}
